import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChildren, inject } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { SenderIdsFormService } from '../../services/sender-ids-form.service';
import { ThirdPartyAssignmentItem } from '../../interfaces/third-party-assignement-item.interface';
import { Subject, filter, takeUntil, tap } from 'rxjs';
import { MatChipsModule } from '@angular/material/chips';
import { DialogService } from '../../services/dialog.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ThirdPartyStatus } from '../../interfaces/third-party-status.interface';

@Component({
  selector: 'si-sender-ids-form',
  templateUrl: './sender-ids-form.component.html',
  styleUrls: ['./sender-ids-form.component.scss'],
  standalone: true,
  imports: [MatFormFieldModule, MatButtonModule, ReactiveFormsModule, CommonModule, MatIconModule, FormsModule, MatInputModule, MatChipsModule, MatTooltipModule],
})
export class SenderIdsFormComponent implements OnDestroy {
  @ViewChildren('senderIdTextInput') autosize: CdkTextareaAutosize;

  @Input() thirdParties: ThirdPartyAssignmentItem[] = [];
  @Input() signUpPage: boolean;
  @Output() thirdPartiesChanged = new EventEmitter<string[][]>();

  private readonly senderIdsFormService = inject(SenderIdsFormService);
  private readonly dialogService = inject(DialogService);
  selectedThirdParties: string[][] = [];

  private _destroy$ = new Subject<void>();

  get formArray() {
    return this.senderIdsFormService.formArray;
  }

  public addNewTextField() {
    this.senderIdsFormService.addControl();
  }

  public getErrorMessage(control: FormControl) {
    if (!control.errors) return '';

    const error = Object.keys(control.errors)[0];

    switch (error) {
      case 'requiredIfSingle':
        return 'Sender ID is required';
      case 'maxlength':
        return 'Sender ID must be at most 11 characters long';
      case 'minlength':
        return 'Sender ID must be at least 3 characters long';
      case 'senderIdValid':
        const errorMessage = Object.values(control.errors)[0];
        return errorMessage;
      default:
        return '';
    }
  }

  public getFormErrorMessage() {
    if (!this.formArray.errors) return;

    const error = Object.keys(this.formArray.errors)[0];

    switch (error) {
      case 'duplicateValues':
        return 'Sender IDs must be unique';
      default:
        return '';
    }
  }

  public handleDeleteTextField(event: MouseEvent, index: number) {
    event.stopPropagation();
    event.preventDefault();

    this.senderIdsFormService.removeControl(index);
    this.selectedThirdParties.splice(index, 1);
  }


  public handleThirdParties(senderIdIndex: number) {
    // Display already selected third parties
    const thirdParties = this.thirdParties.map(({ statusID, organisationName, ...data}) => ({
      ...data,
      organisationName,
      statusID: this.selectedThirdParties[senderIdIndex]?.some(thirdParty => thirdParty === organisationName) ? ThirdPartyStatus.Active : ThirdPartyStatus.Inactive,
    }));
    return this.dialogService.openThirdParties({ thirdParties, returnSelectedOnly: true }).afterClosed()
      .pipe(
        takeUntil(this._destroy$),
        filter((value) => value),
        tap((selectedEntries: ThirdPartyAssignmentItem[]) => {
          this.selectedThirdParties[senderIdIndex] = selectedEntries.map(({ organisationName }) => organisationName);
          this.thirdPartiesChanged.emit(this.selectedThirdParties)
        }),
      ).subscribe();
  }

  public get canAddNewTextField() {
    return this.senderIdsFormService.formArray.valid && this.senderIdsFormService.formArray.length < this.senderIdsFormService.maxSenderIds;
  }


  ngOnDestroy(): void {
    this._destroy$.next();
  }
}
